import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, MapPin, X } from "lucide-react";
import { Seo } from "../components/Seo";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { CtaBand } from "../components/home/CtaBand";
import { Eyebrow, EASE, Reveal } from "../components/motion";
import { CASE_STUDIES, IMAGES, PROJECTS, REALISATION_CATEGORIES } from "../data/content";

export default function Realisations() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("tous");
  const navigate = useNavigate();
  const project = PROJECTS.find((p) => p.id === params.get("projet")) || null;

  const close = () => setParams({});

  useEffect(() => {
    document.body.style.overflow = project ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [project]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const visible = filter === "tous" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <main data-testid="page-realisations">
      <Seo
        title="Réalisations — Suites romantiques, hébergements immersifs & univers à thème | Demoiselle Atypique"
        description="Nos réalisations et celles de nos clients : créer une Love Room, des suites romantiques et des hébergements atypiques rentables en location courte durée."
      />

      <section className="px-6 pb-16 pt-40 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1500px]">
          <Eyebrow>Réalisations</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-serif text-5xl leading-[1.06] text-cream sm:text-6xl lg:text-7xl">
            Des projets qui ne ressemblent à aucun autre.
          </h1>
          <p className="mt-8 max-w-2xl leading-relaxed text-white/55">
            Nos propres réalisations et celles de nos clients : suites romantiques, hébergements immersifs et
            familiaux, univers à thème et locations courte durée. Chaque fiche présente le concept et, lorsque nous les communiquons, les chiffres
            clés et le témoignage du propriétaire.
          </p>
        </div>
      </section>

      <section id="etudes-de-cas" className="scroll-mt-24 px-6 pb-20 sm:px-10 lg:px-16" data-testid="section-etudes-de-cas">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <Eyebrow>Études de cas</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.08] text-cream sm:text-5xl">Tous les chiffres, du prix d'achat au cash-flow.</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {Object.values(CASE_STUDIES).map((cs, i) => (
              <Reveal key={cs.slug} delay={i * 0.08}>
                <Link to={cs.path} data-testid={`case-study-card-${cs.slug}`} className="group relative block overflow-hidden bg-card">
                  <div className="aspect-[16/9] w-full overflow-hidden">
                    <img src={cs.cover} alt={cs.coverAlt} className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-[1.05]" loading="lazy" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon">{cs.eyebrow}</p>
                    <h3 className="mt-2 font-serif text-2xl text-cream sm:text-3xl">{cs.title}</h3>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                      {cs.kpis.slice(0, 2).map((k) => (
                        <p key={k.label} className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/60">
                          <span className="font-serif text-xl normal-case tracking-normal text-neon-light">{k.value}</span> {k.label}
                        </p>
                      ))}
                    </div>
                    <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neon-light">
                      Lire l'étude de cas <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1500px]">
          <div className="flex flex-wrap gap-3" data-testid="realisations-filtres">
            {REALISATION_CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                data-testid={`realisations-filter-${c.id}`}
                className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                  filter === c.id ? "border-neon bg-neon text-ink" : "border-white/20 text-white/60 hover:border-neon hover:text-neon"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.07} className="h-full">
                <button
                  onClick={() => setParams({ projet: p.id })}
                  data-testid={`realisation-card-${p.id}`}
                  className="group relative block h-full w-full overflow-hidden bg-card text-left"
                >
                  <div className="aspect-[4/3] w-full overflow-hidden">
                    <img
                      src={p.cover}
                      alt={`${p.name} — ${p.type} : ${p.concept}`}
                      className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-[1.05]"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon">{p.type}</p>
                      <h2 className="mt-2 font-serif text-2xl text-cream">{p.name}</h2>
                      <p className="mt-1 text-xs text-white/50">{p.location}</p>
                    </div>
                    {p.rating && <p className="shrink-0 font-mono text-xs text-neon-light">★ {p.rating}</p>}
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1D1327] py-24 lg:py-32">
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-16">
          <Reveal>
            <Eyebrow>Avant / Après</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.08] text-cream sm:text-5xl">
              Nous ne voyons pas seulement un bien. Nous voyons son potentiel.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mx-auto mt-12 max-w-5xl">
              <BeforeAfterSlider
                before={IMAGES.avantApres.avant}
                after={IMAGES.avantApres.apres}
                altBefore="Appartement vide de Charles à Douai avant travaux — murs blancs et parquet stratifié"
                altAfter="L'Éclipse après transformation — suite privée de luxe avec enseigne lunaire, sauna et néons violets"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
                La Black Room de Charles, devenue L'Éclipse — Douai · photos réelles du projet
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />

      <AnimatePresence>
        {project && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
            data-testid="realisation-modal"
            className="fixed inset-0 z-[80] flex items-end justify-center bg-ink/90 backdrop-blur-sm sm:items-center sm:p-8"
          >
            <motion.article
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[92vh] w-full max-w-4xl overflow-y-auto border border-white/10 bg-[#1D1327]"
            >
              <button
                onClick={close}
                data-testid="realisation-modal-close"
                aria-label="Fermer la fiche projet"
                className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center border border-white/20 bg-ink/70 text-cream backdrop-blur transition-colors hover:border-neon hover:text-neon"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="aspect-[16/9] w-full overflow-hidden">
                <img src={project.cover} alt={`${project.name} — ${project.type}`} className="h-full w-full object-cover" />
              </div>
              <div className="p-8 lg:p-12">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neon">
                  <span>{project.type}</span>
                  <span className="flex items-center gap-2 text-white/40">
                    <MapPin className="h-3 w-3" /> {project.location}
                  </span>
                  {project.rating && <span className="text-neon-light">★ {project.rating} — note voyageurs</span>}
                </div>
                <h2 className="mt-4 font-serif text-4xl text-cream lg:text-5xl">{project.name}</h2>
                <p className="mt-6 leading-relaxed text-white/60">{project.concept}</p>

                {project.figures.length > 0 && (
                  <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4" data-testid="realisation-figures">
                    {project.figures.map((f) => (
                      <div key={f.label} className="border-l border-neon/40 pl-4">
                        <p className="font-serif text-3xl text-cream">{f.value}</p>
                        <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">{f.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                {project.testimonial && (
                  <blockquote className="mt-10 border-l-2 border-neon pl-6" data-testid="realisation-testimonial">
                    <p className="font-serif text-xl italic leading-relaxed text-white/70">« {project.testimonial.texte} »</p>
                    <footer className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neon">{project.testimonial.auteur}</footer>
                  </blockquote>
                )}

                {project.gallery.length > 0 && (
                  <div className="mt-10 grid grid-cols-2 gap-4" data-testid="realisation-gallery">
                    {project.gallery.map((g, i) => {
                      const item = typeof g === "string" ? { src: g } : g;
                      return (
                        <div key={i} className="relative overflow-hidden">
                          <img
                            src={item.src}
                            alt={`${project.name} — ${item.tag || `vue ${i + 1} du projet`}`}
                            className="aspect-[4/3] w-full object-cover"
                            loading="lazy"
                          />
                          {item.tag && (
                            <span className="absolute left-3 top-3 bg-ink/80 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-neon-light backdrop-blur">
                              {item.tag}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-10 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
                  <p className="font-serif text-xl italic text-white/50">Un projet similaire en tête ?</p>
                  <div className="flex flex-wrap gap-3">
                    {project.caseStudy && (
                      <Link to={project.caseStudy} data-testid="realisation-modal-case-study" className="btn-outline">
                        Lire l'étude de cas <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                    <button onClick={() => navigate("/rendez-vous")} data-testid="realisation-modal-cta" className="btn-primary">
                      Parler de mon projet <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
